<template>
  <div class="tags-settings">
    <modal
      v-if="isShowSettings"
      :has-footer="false"
      class-wrapper="tags-settings__modal"
      redesign
      small
      @close="$emit('close')"
    >
      <div slot="header">
        <h3>{{ $tr('vcv_tags.settings.title') }}</h3>
      </div>

      <div slot="body">
        <div class="tags-settings__settings">
          <p>{{ $tr('vcv_tags.settings.desc') }}</p>

          <div class="tags-settings__search-group">
            <input
              v-model="settingsSearch"
              class="tags-settings__search"
              type="text"
              :placeholder="$tr('search')"
              :disabled="isFetching"
              @keyup.enter="addNewTag"
            />

            <vpreloader v-if="isFetching" size="24" class="tags-settings__search-loader" />

            <vbutton
              v-if="!isTagExist"
              class=""
              size="small"
              label="↵"
              text_color="white"
              bg_color="green"
              :disabled="isSaving"
              :loading="isSaving"
              @click="addNewTag"
            />
          </div>

          <div class="settings-list">
            <template v-if="getSearchSuggestions.length > 0">
              <div v-for="(item, index) in getSearchSuggestions" :key="index" class="settings-list__item">
                <vcv-tags-item v-if="editingTags.indexOf(item.id) === -1" :text="item.name" />

                <div v-else class="tags-settings__input">
                  <input
                    :id="`tag-${item.id}`"
                    :value="item.name"
                    type="text"
                    :disabled="isSaving"
                    @keyup.enter="editTag(item)"
                  />
                </div>

                <div
                  v-if="editingTags.indexOf(item.id) === -1"
                  class="settings-list__actions"
                  :class="{ disabled: isSaving }"
                >
                  <i class="icon-settings" @click="setEditMode(item.id)"></i>
                  <i class="icon-recycle_bin" @click="removeTag(item)"></i>
                </div>

                <div v-else class="settings-list__actions">
                  <div class="tags-settings__modal-buttons">
                    <vbutton
                      size="small"
                      text_color="grey"
                      color="grey"
                      bg_color="white"
                      label="common.cancel"
                      :loading="isSaving"
                      @click="setEditMode(item.id, 'remove')"
                    />

                    <vbutton
                      size="small"
                      label="common.ok"
                      text_color="white"
                      bg_color="green"
                      :loading="isSaving"
                      @click="editTag(item)"
                    />
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </modal>

    <modal
      v-if="dialogRemove"
      :has-footer="false"
      class-wrapper="tags-settings__modal"
      redesign
      small
      @close="dialogRemove = false"
    >
      <div slot="header">
        <h3 class="text-center">{{ $tr('vcv_tags.delete.title') }} "{{ removeItem.name }}" ?</h3>
      </div>

      <div slot="body">
        <div class="tags-settings__modal-body">{{ $tr('vcv_tags.delete.desc') }}</div>

        <div class="tags-settings__modal-buttons">
          <vbutton
            text_color="grey"
            color="grey"
            bg_color="white"
            label="common.cancel"
            @click="dialogRemove = false"
          />

          <vbutton
            icon="icon-bin-red"
            label="common.delete"
            :loading="isSaving"
            text_color="red"
            bg_color="white"
            color="grey"
            @click="confirmRemove"
          />
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import Axios from 'axios';
import Modal from '@/components/Modal.vue';
import VcvTagsItem from './VcvTagsItem.vue';

export default {
  name: 'VcvTagsSettings',

  components: {
    Modal,
    VcvTagsItem
  },

  props: {
    isShowSettings: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isFetching: false,
      isSaving: false,
      settingsTags: [],
      settingsSearch: [],
      dialogRemove: false,
      removeItem: null,
      editingTags: []
    };
  },

  computed: {
    getSearchSuggestions() {
      if (this.settingsSearch.length > 0) {
        return this.settingsTags.filter(x => x.name.toLowerCase().indexOf(this.settingsSearch.toLowerCase()) !== -1);
        // .map(x => {
        //   return {
        //     ...x,
        //     name: x.name.replace(
        //       new RegExp(`${this.settingsSearch}`, 'gi'),
        //       `<strong>${this.settingsSearch}</strong>`
        //     )
        //   };
        // });
      } else {
        return this.settingsTags;
      }
    },

    isTagExist() {
      if (this.settingsSearch.length > 0) {
        return this.settingsTags.some(x => x.name.toLowerCase() === this.settingsSearch.toLowerCase());
      }

      return true;
    }
  },

  watch: {
    dialogRemove(val) {
      if (!val) this.removeItem = null;
    },

    isShowSettings(val) {
      if (val) this.fetchAllTags();
    }
  },

  async created() {
    await this.fetchAllTags();
  },

  methods: {
    async fetchAllTags() {
      this.isFetching = true;

      Axios.get('/v3/tags', { page: 1, limit: 9999, with: ['tag'] })
        .catch(err => {
          this.$store.dispatch('global/setError', err, { root: true });
        })
        .then(response => {
          this.settingsTags = response.data._embedded.tags;
        })
        .finally(() => {
          this.isFetching = false;
        });
    },

    setEditMode(tag, type = 'add') {
      if (type === 'add' && this.editingTags.indexOf(tag) === -1) {
        this.editingTags.push(tag);
      }
      if (type === 'remove') {
        this.editingTags = this.editingTags.filter(item => item !== tag);
      }
    },

    async editTag(tag) {
      this.isSaving = true;
      const newTagName = document.getElementById(`tag-${tag.id}`).value;

      await Axios.patch(`/v3/tags/${tag.id}`, { name: newTagName, sort_order: 0 })
        .then(response => {
          if (response.status === 200) {
            this.setEditMode(tag.id, 'remove');

            const tagIndex = this.settingsTags.findIndex(x => x.id === response.data.id);
            if (tagIndex >= 0) {
              this.settingsTags[tagIndex].name = newTagName;
            }

            this.$emit('fetch-response-tags');
          }
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    removeTag(item) {
      this.dialogRemove = true;
      this.removeItem = item;
    },

    async confirmRemove() {
      this.isSaving = true;

      await Axios.delete(`/v3/tags/${this.removeItem.id}`)
        .catch(err => {
          this.$store.dispatch('global/setError', err, { root: true });
        })
        .then(() => {
          this.$emit('fetch-response-tags');
          this.dialogRemove = false;
        })
        .finally(() => {
          this.isSaving = false;
        });
    },

    async addNewTag() {
      if (!this.isTagExist) {
        this.isSaving = true;

        await Axios.post('/v3/tags', { name: this.settingsSearch })
          .catch(err => {
            this.$store.dispatch('global/setError', err, { root: true });
          })
          .then(async response => {
            if (response.status === 200) {
              this.settingsTags.push(response.data);
              this.settingsSearch = '';
            }
          })
          .finally(() => {
            this.isSaving = false;
          });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.tags-settings {
  &__modal {
    ::v-deep .modal-container {
      padding: 40px 50px;
      width: 500px !important;
      min-width: 360px !important;
      max-height: 80%;
      overflow-y: auto;
    }
  }

  &__modal-body {
    @include mixin-text-main;
    font-size: 16px;
    text-align: center;
    margin-bottom: 20px;
  }

  &__modal-buttons {
    display: flex;
    align-items: center;
    justify-content: space-between;

    > *:first-child {
      margin-right: 5px;
    }
  }

  &__search-group {
    position: relative;
    display: flex;

    .vbutton {
      height: 40px !important;
      width: 40px !important;
    }
  }

  &__search {
    width: 100%;
    border: 1px solid $grey-light;
    transition: border 0.3s;

    &:focus {
      border: 1px solid $green;
      transition: border 0.3s;
    }
  }

  &__search-loader {
    position: absolute;
    top: 8px;
    right: 10px;
  }

  &__input {
    border-radius: 4px;
    color: $grey;
    display: flex;
    max-width: 184px;
    height: 32px;
    border: 1px solid $grey-light;
    position: relative;

    input:not(.multiselect__input) {
      border: 0;
      background: $grey-lighter;
      color: $main-dark;
      height: 100% !important;
      line-height: 1;
      padding: 0 25px 0 10px;
      width: 100%;
      border: 1px solid transparent;
      transition: border 0.3s;

      &:focus {
        border: 1px solid $green;
        transition: border 0.3s;
      }
    }
  }
}

.settings-list {
  margin-top: 16px;

  &__item {
    position: relative;
    margin-bottom: 8px;
    display: flex;
    align-items: center;

    .vcv-tag {
      margin-bottom: 0;
    }
  }

  &__actions {
    margin-left: 8px;
    display: flex;
    align-items: center;
    opacity: 1;
    pointer-events: all;
    transition: opacity 0.3s;

    &.disabled {
      opacity: 0.6;
      pointer-events: none;
      transition: opacity 0.3s;
    }

    i {
      color: $grey;
      margin-right: 8px;
      cursor: pointer;
      transition: color 0.3s;

      &:first-child:hover {
        color: $green-hover;
        transition: color 0.3s;
      }

      &:last-child:hover {
        color: $red;
        transition: color 0.3s;
      }
    }
  }
}
</style>
